/**
 * The fixed-timestep loop.
 *
 * The simulation advances in whole TICK_MS steps and never sees a variable delta. The
 * renderer runs at whatever rate the browser gives us and reads interpolated state from
 * the temporal smoothers, so the two rates are fully independent.
 *
 * The one deliberate divergence from the original is MAX_CATCHUP_TICKS — see constants.ts.
 */

import { MAX_CATCHUP_TICKS, TICK_MS } from './constants';

export interface LoopCallbacks {
  /** One simulation step. Always exactly TICK_MS of game time. */
  tick(simTimeMs: number): void;
  /**
   * Draw one frame.
   * @param simTimeMs simulation time at the end of the last completed tick
   * @param frameMs   wall-clock milliseconds since the previous rendered frame
   */
  render(simTimeMs: number, frameMs: number): void;
}

export interface LoopStats {
  /** Ticks executed in the most recent frame. */
  ticksLastFrame: number;
  /** Ticks discarded because catch-up hit its cap. Non-zero means we are running behind. */
  droppedTicks: number;
  simTimeMs: number;
  fps: number;
}

/**
 * Pure accumulator arithmetic, extracted so the catch-up policy is testable without a
 * browser. Given the leftover time and this frame's elapsed time, decide how many ticks to
 * run, how many to abandon, and what carries over.
 */
export function advance(
  accumulatorMs: number,
  frameMs: number,
): { ticks: number; dropped: number; remainderMs: number } {
  // A hidden tab, a breakpoint, or a long GC can hand us an enormous delta. Discard it
  // rather than trying to simulate through it.
  const delta = frameMs > 1000 ? TICK_MS : frameMs;
  let acc = accumulatorMs + delta;

  const wanted = Math.floor(acc / TICK_MS);
  const ticks = Math.min(wanted, MAX_CATCHUP_TICKS);
  acc -= ticks * TICK_MS;

  let dropped = 0;
  if (acc >= TICK_MS) {
    dropped = Math.floor(acc / TICK_MS);
    acc -= dropped * TICK_MS;
  }

  return { ticks, dropped, remainderMs: acc };
}

export class FixedLoop {
  private readonly cb: LoopCallbacks;
  private accumulatorMs = 0;
  private simTimeMs = 0;
  private lastWallMs = 0;
  private rafHandle: number | null = null;
  private running = false;
  /** False until the first tick has run. See runFrame. */
  private started = false;

  readonly stats: LoopStats = {
    ticksLastFrame: 0,
    droppedTicks: 0,
    simTimeMs: 0,
    fps: 0,
  };

  private fpsAccum = 0;
  private fpsFrames = 0;

  constructor(callbacks: LoopCallbacks) {
    this.cb = callbacks;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.started = false;
    this.lastWallMs = performance.now();
    this.accumulatorMs = 0;
    this.schedule();
  }

  stop(): void {
    this.running = false;
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }

  /** Current simulation time in milliseconds. Advances only in TICK_MS increments. */
  get time(): number {
    return this.simTimeMs;
  }

  private schedule(): void {
    this.rafHandle = requestAnimationFrame(() => this.frame());
  }

  private frame(): void {
    if (!this.running) return;
    const now = performance.now();
    const frameMs = now - this.lastWallMs;
    this.lastWallMs = now;
    // Queue the next frame BEFORE running this one. A frame that throws then still leaves
    // the loop alive: the error shows up in the console every frame, which is loud and
    // fixable, instead of silently killing the game and leaving a black screen forever.
    this.schedule();
    this.runFrame(frameMs);
  }

  /**
   * Run one frame with an explicit elapsed time, without scheduling another.
   *
   * The browser does not fire animation frames for a hidden tab, which is right for a game
   * but means neither an automated screenshot harness nor a headless test can drive the
   * loop. This is the way in for both.
   */
  runFrame(frameMs: number): void {
    const { ticks, dropped, remainderMs } = advance(this.accumulatorMs, frameMs);
    this.accumulatorMs = remainderMs;

    // The very first animation frame usually arrives sooner than a whole tick, which would
    // mean drawing a simulation that has never run a step — there is nothing to draw, and
    // anything reading interpolated state has no samples to interpolate. So the first tick
    // is guaranteed rather than left to the timing of the first frame.
    const firstTicks = this.started ? ticks : Math.max(ticks, 1);
    this.started = true;

    for (let i = 0; i < firstTicks; i++) {
      this.simTimeMs += TICK_MS;
      this.cb.tick(this.simTimeMs);
    }

    // Dropped ticks mean we could not keep up: the match clock slips rather than the loop
    // spiralling. See constants.ts MAX_CATCHUP_TICKS.
    this.stats.droppedTicks += dropped;
    this.stats.ticksLastFrame = firstTicks;
    this.stats.simTimeMs = this.simTimeMs;

    this.fpsAccum += frameMs;
    this.fpsFrames++;
    if (this.fpsAccum >= 500) {
      this.stats.fps = (this.fpsFrames * 1000) / this.fpsAccum;
      this.fpsAccum = 0;
      this.fpsFrames = 0;
    }

    this.cb.render(this.simTimeMs, frameMs);
  }
}
